import React, {useEffect} from 'react';
import useGetUserLocation from '../../hooks/useUserLocation';
import {getVenueDistanceText} from '../../utils/venueDistance';

// Define interfaces for venue data
interface Venue {
  name: string;
  latitude: number;
  longitude: number;
  description?: string;
  actionLink?: string;
  [key: string]: any; // Allow for other properties
}

interface VenueData {
  venue: Venue;
  index: number;
}

/**
 * Component that enhances venue listings with distance information
 * This component finds all venue summary elements in the DOM and adds distance text
 */
const VenueDistanceEnhancer: React.FC = () => {
  const userLocation = useGetUserLocation();

  useEffect(() => {
    //debugLog('Effect triggered', userLocation?.data ? 'User location available' : 'No user location yet');

    // Only proceed if we have user location data
    if (!userLocation?.data) {
      //debugLog('No user location data available yet');
      return;
    }

    //console.log('User location data available:', userLocation.data);

    // Function to add distance information to venue summaries - SIMPLIFIED VERSION
    const addDistanceInfo = () => {
      try {
        // Try to get venue data from multiple possible sources
        let mapViewData = document.querySelector('div[data-map-venues]');

        // If not found, try the place-listings-specific attribute
        if (!mapViewData) {
          debugLog('No map venue data found in data-map-venues, trying place listings');
          mapViewData = document.querySelector('div[data-place-listings-map-venues]');
          if (!mapViewData) {
            debugLog('No venue data found in any container');
            return;
          }
          // debugLog('Found venue data in place-listings container');
        } else {
          // debugLog('Found venue data in map-venues container');
        }

        //console.log('VenueDistanceEnhancer looking for venue data...');

        try {
          // Parse venue data from whichever attribute we found
          const attrName = mapViewData.hasAttribute('data-map-venues')
            ? 'data-map-venues'
            : 'data-place-listings-map-venues';
          const venuesData = mapViewData.getAttribute(attrName) || '[]';
          // debugLog('Raw venue data:', venuesData.substring(0, 100) + '...');
          const parsedData = JSON.parse(venuesData);

          // Handle either an array or object with places property
          const venues = Array.isArray(parsedData)
            ? parsedData
            : parsedData.places && Array.isArray(parsedData.places)
            ? parsedData.places
            : [];

          if (!venues.length) {
            debugLog('No venue data found in parsed JSON');
            return;
          } // Find all venue summaries in the place listings
          // Use a more inclusive selector that finds all details elements after the "by-place" heading
          // Or directly find all details elements if there's no specific heading
          const venueDetails = document.querySelectorAll(
            '#by-place ~ details, [id="by-place"] ~ details, details'
          );
          const venueMap = new Map(); // We've already handled array vs object structure when creating 'venues'
          const venueArray = venues;

          // Create a function to normalize venue names for better matching
          const normalizeVenueName = (name: string) => {
            // Convert HTML entities in the name to their character representation
            const tempElement = document.createElement('div');
            tempElement.innerHTML = name;
            const decodedName = tempElement.textContent || name;
            return decodedName.trim().toLowerCase();
          };

          // Add venues to map with both original and normalized name as keys
          venueArray.forEach((venue) => {
            if (venue && venue.name) {
              const normalizedName = normalizeVenueName(venue.name);
              venueMap.set(venue.name.trim(), venue); // Original name
              venueMap.set(normalizedName, venue); // Normalized name
              // debugLog(`Added venue to map: ${venue.name} (normalized: ${normalizedName})`);
            }
          });

          venueDetails.forEach((detail, detailIndex) => {
            try {
              // Get the summary element (which contains the venue name)
              const summary = detail.querySelector('summary');
              if (!summary) {
                // console.log(`No summary found for venue ${detailIndex}`);
                return;
              }

              // Find the venue name text
              const venueNameSpan = summary.querySelector('span[style*="display: flex"]');
              if (!venueNameSpan) {
                // console.log(`No venue name span found for venue ${detailIndex}`);
                return;
              }

              // Create a clone of the venue name span to extract clean text
              const venueNameSpanClone = venueNameSpan.cloneNode(true) as HTMLElement;

              // Remove any existing distance indicators from the clone
              const distanceElements = venueNameSpanClone.querySelectorAll('.venue-distance');
              distanceElements.forEach((el) => el.parentNode?.removeChild(el));

              // Get the textContent before operations for logging
              let fullText = venueNameSpan.textContent || '';

              // Now get the clean venue name from the clone (remove leading number)
              const venueNameText = venueNameSpanClone.textContent?.replace(/^\d+\s*/, '').trim();

              // Check if the original has a distance indicator for later reference
              const existingDistanceIndicator = venueNameSpan.querySelector('.venue-distance');

              if (!venueNameText) {
                //console.log(`Could not extract venue name for detail ${detailIndex}`);
                return;
              } // Normalize the extracted venue name the same way
              const normalizedVenueNameText = (() => {
                const tempElement = document.createElement('div');
                tempElement.innerHTML = venueNameText || '';
                return (tempElement.textContent || venueNameText || '').trim().toLowerCase();
              })();

              // Find the matching venue from our venue data - try both original and normalized name
              let venue = venueMap.get(venueNameText);
              if (!venue) {
                // Try with normalized name
                venue = venueMap.get(normalizedVenueNameText);
              }
              if (!venue || !venue.latitude || !venue.longitude) {
                debugLog(
                  `No matching venue data found for "${venueNameText}" (normalized: "${normalizedVenueNameText}")`
                );
                return;
              }

              // Calculate the distance text
              const distanceText = getVenueDistanceText(userLocation.data, {
                latitude: venue.latitude,
                longitude: venue.longitude,
              });
              if (!distanceText) {
                //console.log(`No distance text calculated for venue ${detailIndex}`);
                return;
              }

              //console.log(`Distance for ${venue.name}: ${distanceText}`);              // Check if we already added a distance indicator within venueNameSpan
              const existingIndicator = venueNameSpan.querySelector('.venue-distance');
              if (existingIndicator) {
                existingIndicator.innerHTML = `${distanceText}`;

                return;
              }

              // FIX: Insert the distance indicator INSIDE the summary element instead of after it
              // This way the distance shows up as part of the venue header              // Create the distance indicator as a span (not div) to fit inside the summary
              const distanceIndicator = document.createElement('span');
              distanceIndicator.className = 'venue-distance';
              distanceIndicator.style.position = 'absolute';
              distanceIndicator.style.bottom = '2px';
              distanceIndicator.style.left = '48px'; // Position it to the right of the venue name
              distanceIndicator.style.fontSize = '12px';
              // Use white text for Insiders Club Lounge (which has gradient background), grey for others              // Check if the venue is featured - multiple detection methods              // Use only the featured property from JSON data - no style-based detection
              const isFeatured = venue.featured === true;

              distanceIndicator.style.color = isFeatured ? 'white' : '#666';
              distanceIndicator.style.fontWeight = isFeatured ? 'bold' : 'normal';
              distanceIndicator.style.marginLeft = '10px'; // Space from venue name
              distanceIndicator.style.display = 'inline-block'; // Display inline with venue name
              distanceIndicator.style.verticalAlign = 'middle'; // Align with text

              // Add text shadow for featured places to make it more visible on gradients
              // if (robustFeatured) {
              //   distanceIndicator.style.textShadow = '0 1px 2px rgba(0, 0, 0, 0.3)';
              // }

              // Add a small location icon (using Unicode map pin) for visual clarity
              distanceIndicator.innerHTML = `${distanceText}`;

              // Use the previously defined venueNameSpan instead of redeclaring it
              if (venueNameSpan) {
                // Insert inside the flex container with venue name
                venueNameSpan.appendChild(distanceIndicator);
              } else {
                // Fallback: append directly to the summary
                summary.appendChild(distanceIndicator);
              }
              //console.log(`Added distance indicator for ${venue.name}: ${distanceText}`);
            } catch (venueError) {
              console.error(`Error processing venue ${detailIndex}:`, venueError);
              // Continue with next venue even if this one fails
            }
          });
        } catch (error) {
          console.error('Error parsing venue data:', error);
        }
      } catch (error) {
        console.error('Error adding distance information:', error);
      }
    }; // Initial addition of distance info with retry mechanism
    const maxRetries = 3;
    let retries = 0;

    const tryAddDistance = () => {
      try {
        addDistanceInfo();
      } catch (error) {
        console.error('Error adding distance info:', error);
        if (retries < maxRetries) {
          retries++;
          //console.log(`Retrying distance enhancement (${retries}/${maxRetries})...`);
          setTimeout(tryAddDistance, 500);
        }
      }
    };
    setTimeout(tryAddDistance, 500); // Initial delay to ensure DOM is ready

    // Set up a MutationObserver to watch for changes to the DOM
    // This ensures we add distance info even if content is dynamically loaded
    const observer = new MutationObserver((mutations: MutationRecord[]) => {
      const shouldUpdate = mutations.some((mutation) => {
        return (
          // Check for newly added elements
          (mutation.type === 'childList' &&
            Array.from(mutation.addedNodes).some((node) => {
              // Check if the node is an Element
              if (node.nodeType !== Node.ELEMENT_NODE) return false;

              const element = node as Element;
              return (
                // When the by-place section is added
                element.id === 'by-place' ||
                (element.querySelector && element.querySelector('[id="by-place"]')) ||
                // When details or summary elements are added
                element.tagName === 'DETAILS' ||
                element.tagName === 'SUMMARY' ||
                // Check for venue containers
                element.classList?.contains('venue-container')
              );
            })) ||
          // Also check for attribute changes on map data
          (mutation.type === 'attributes' &&
            mutation.target instanceof Element &&
            mutation.target.hasAttribute('data-map-venues'))
        );
      });

      if (shouldUpdate) {
        setTimeout(addDistanceInfo, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-map-venues'],
    });

    return () => {
      observer.disconnect();
    };
  }, [userLocation?.data]);

  // This component doesn't render anything itself
  return null;
};

// Debug configuration and function
const DEBUG_ENABLED = true;

function debugLog(message: string, data?: any) {
  if (DEBUG_ENABLED) {
    console.log(`[VenueDistanceEnhancer] ${message}`, data || '');
  }
}

export default VenueDistanceEnhancer;
